/**
 * REST API klient pro PIP měnič. Tenké axios wrappery.
 */

import { deleteAsync, getAsync, patchAsync, postAsync } from "./axios";
import type {
  PipBatteryType,
  PipDiscoveredSerial,
  PipListItem,
  PipProbeResult,
  PipProtocolVariant,
  PipRecord,
  PipRuntime,
  PipTransportType,
} from "@client/types/pip";
import type { WriteBatchResult, WriteFieldResult } from "@client/types/verify";

export const getPipList = () => getAsync<ApiResp<PipListItem[]>>("/api/pip");

export const getPipSnapshot = (slug: string) =>
  getAsync<ApiResp<{ record: PipRecord; runtime: PipRuntime | null }>>(`/api/pip/${slug}/snapshot`);

export const getPipSerialPorts = () => getAsync<ApiResp<PipDiscoveredSerial[]>>("/api/pip/serial-ports");

export interface PipDebugFrame {
  length: number;
  hex: string;
  ascii: string;
  timestamp: number;
}

export const getPipDebugFrame = (slug: string) => getAsync<ApiResp<PipDebugFrame>>(`/api/pip/${slug}/debug`);

export interface ProbePayload {
  transportType: PipTransportType;
  transportTarget: string;
  baudRate?: number;
  timeoutMs?: number;
}

export const postPipProbe = (data: ProbePayload) => postAsync<ApiResp<PipProbeResult>>("/api/pip/probe", data);

export interface CreatePipPayload {
  slug: string;
  name: string;
  transportType: PipTransportType;
  transportTarget: string;
  protocolVariant?: PipProtocolVariant;
  baudRate?: number;
  batteryType?: PipBatteryType;
  batteryNominalV?: number;
  notes?: string | null;
  enabled?: boolean;
  order?: number | null;
}

export const postPip = (data: CreatePipPayload) => postAsync<ApiResp<PipRecord>>("/api/pip", data);

export interface UpdatePipPayload {
  name?: string;
  transportType?: PipTransportType;
  transportTarget?: string;
  protocolVariant?: PipProtocolVariant;
  baudRate?: number;
  batteryType?: PipBatteryType;
  batteryNominalV?: number;
  notes?: string | null;
  enabled?: boolean;
  order?: number | null;
}

export const patchPip = (slug: string, data: UpdatePipPayload) =>
  patchAsync<ApiResp<PipRecord>>(`/api/pip/${slug}`, data);

export const deletePipReq = (slug: string) => deleteAsync<ApiResp<PipRecord>>(`/api/pip/${slug}`);

export const postPipReconnect = (slug: string) => postAsync<ApiResp<boolean>>(`/api/pip/${slug}/reconnect`);

// Control endpointy — vrací ověřený per-field výsledek (read-back z měniče)
export const postPipOutputPriority = (slug: string, value: "00" | "01" | "02") =>
  postAsync<ApiResp<WriteFieldResult>>(`/api/pip/${slug}/control/output-priority`, { value });

export const postPipChargerPriority = (slug: string, value: "00" | "01" | "02" | "03") =>
  postAsync<ApiResp<WriteFieldResult>>(`/api/pip/${slug}/control/charger-priority`, { value });

export const postPipMaxChargingCurrent = (slug: string, amps: number) =>
  postAsync<ApiResp<WriteFieldResult>>(`/api/pip/${slug}/control/max-charging-current`, { amps });

export const postPipMaxUtilityCurrent = (slug: string, amps: number) =>
  postAsync<ApiResp<WriteFieldResult>>(`/api/pip/${slug}/control/max-utility-current`, { amps });

// Settings batch PATCH
export interface PipSettingsPatch {
  batteryType?: PipBatteryType;
  shutdownV?: number;
  bulkV?: number;
  floatV?: number;
  rechargeV?: number;
  redischargeV?: number;
  maxTotalChargingA?: number;
  maxUtilityChargingA?: number;
  outputPriority?: "00" | "01" | "02";
  chargerPriority?: "00" | "01" | "02" | "03";
  gridRange?: "00" | "01";
  outputMode?: "00" | "01" | "02" | "03" | "04";
}

export const patchPipSettings = (slug: string, data: PipSettingsPatch) =>
  patchAsync<ApiResp<WriteBatchResult>>(`/api/pip/${slug}/settings`, data);
